/**
 * KV Client Utilities
 * Client-side functions to interact with Cloudflare KV for hunt data
 */

/**
 * Fetch hunt data from KV
 * @returns {Promise<Object|null>} Hunt object or null if not found/error
 */
export async function fetchHunt() {
  try {
    const response = await fetch('/api/hunt');
    if (!response.ok) {
      console.warn(`Fetch hunt failed: ${response.status}`);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching hunt:', error);
    return null;
  }
}

/**
 * Save hunt progress (found treasures, state)
 * @param {Object} progress - Progress data to save
 * @returns {Promise<Object>} Response object with success status
 */
export async function saveHuntProgress(progress) {
  try {
    const response = await fetch('/api/hunt/progress', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(progress),
    });

    if (!response.ok) {
      console.error(`Failed to save progress: ${response.status}`);
      return { error: 'Failed to save progress' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving hunt progress:', error);
    return { error: 'Failed to save progress' };
  }
}

/**
 * Mark a treasure as found in KV
 * @param {string} treasureId - ID of treasure to mark as found
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export async function markTreasureFound(treasureId) {
  try {
    const response = await fetch('/api/hunt/treasure', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ treasureId }),
    });

    if (!response.ok) {
      console.error(`Failed to mark treasure found: ${response.status}`);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error marking treasure found:', error);
    return false;
  }
}

/**
 * Save final puzzle completion
 * @param {Array<string>} correctWords - Array of correct puzzle words
 * @returns {Promise<Object>} Response object with completion status
 */
export async function savePuzzleCompletion(correctWords) {
  try {
    const response = await fetch('/api/hunt/puzzle/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ correctWords }),
    });

    if (!response.ok) {
      console.error(`Failed to complete puzzle: ${response.status}`);
      return { error: 'Failed to complete puzzle' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving puzzle completion:', error);
    return { error: 'Failed to complete puzzle' };
  }
}
